<script setup lang="ts">
    import {
        AlertDialog,
        AlertDialogAction,
        AlertDialogCancel,
        AlertDialogContent,
        AlertDialogDescription,
        AlertDialogFooter,
        AlertDialogHeader,
        AlertDialogTitle,
    } from '@/components/ui/alert-dialog';
    import type { Voucher, SharedData } from '@/types';
    import { toast } from 'vue-sonner';
    import { useForm } from '@inertiajs/vue3';
    import { useI18n } from 'vue-i18n';
    import { useVoucherStore } from '@/stores/voucher';
    import { useQueryClient } from '@tanstack/vue-query';
    import QueryKey from '@/enums/QueryKey';
    import type { Table } from '@tanstack/vue-table';
    import { computed, watch } from 'vue';
    import { usePage } from '@inertiajs/vue3';

    const { t } = useI18n();

    const voucherStore = useVoucherStore();
    const queryClient = useQueryClient();

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth?.user.selected_company_id);

    const props = defineProps<{
        table: Table<Voucher>;
    }>();

    const form = useForm({
        ids: [] as number[],
    });

    watch(
        () => voucherStore.validVoucherIds,
        (newIds) => {
            form.ids = newIds;
        },
        { immediate: true },
    );

    const close = () => {
        form.post(route('v2.voucher.admin.close.selection'), {
            preserveScroll: true,
            onSuccess: () => {
                toast.success(t('vouchers.select.closed.success'));
                props.table.resetRowSelection();
                queryClient.invalidateQueries({
                    queryKey: [QueryKey.VOUCHERS, selectedCompanyId.value],
                });
            },
        });
    };
</script>

<template>
    <AlertDialog
        :open="voucherStore.closeAlertOpen"
        @update:open="(value) => voucherStore.toggleOpen('close', value)"
    >
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>{{ t('vouchers.select.close_confirm.title') }}</AlertDialogTitle>
                <AlertDialogDescription class="flex flex-col gap-2">
                    <i18n-t
                        keypath="vouchers.select.close_confirm.content"
                        tag="span"
                        scope="global"
                    >
                        <template #body>
                            <span class="font-bold">
                                {{ voucherStore.validVoucherNumbers }}
                            </span>
                        </template>
                    </i18n-t>

                    {{ t('vouchers.select.close_confirm.description') }}
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel>{{ t('forms.no') }}</AlertDialogCancel>
                <AlertDialogAction
                    variant="default"
                    @click="close"
                >
                    {{ t('forms.yes') }}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
</template>
